import {useCallback} from "react";
import {useSearchParams} from "react-router-dom";
import {reverseGeocode} from "@/api/apiClient";
import type {LocationWithCoordinates} from "@/features/location-suggestions/utils/types";
import type {OrderDraft} from "../utils/types";

function parseCoord(value: string | null): number | null {
    if (!value) return null;
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : null;
}

/**
 * Hook for reading order-related URL parameters.
 *
 * Returns synchronously available draft values (name, phoneNumber) and
 * an async function to resolve coordinates into locations via reverse geocoding.
 */
export function useUrlOrderParams() {
    const [searchParams] = useSearchParams();

    const name = searchParams.get("name")?.trim() || null;
    const phoneNumber = searchParams.get("phoneNumber")?.trim() || null;

    const pickupLat = parseCoord(searchParams.get("pickupLat"));
    const pickupLon = parseCoord(searchParams.get("pickupLon"));
    const dropOffLat = parseCoord(searchParams.get("dropOffLat"));
    const dropOffLon = parseCoord(searchParams.get("dropOffLon"));

    const hasPickupCoords = pickupLat !== null && pickupLon !== null;
    const hasDropOffCoords = dropOffLat !== null && dropOffLon !== null;

    const urlDraft: Partial<OrderDraft> = {};
    if (name) urlDraft.name = name;
    if (phoneNumber) urlDraft.phoneNumber = phoneNumber;

    const hasUrlParams = !!name || !!phoneNumber || hasPickupCoords || hasDropOffCoords;

    const resolveLocations = useCallback(async (): Promise<Partial<OrderDraft>> => {
        if (!hasPickupCoords && !hasDropOffCoords) return {};

        const [pickupResult, dropoffResult] = await Promise.allSettled([
            hasPickupCoords
                ? reverseGeocode({coordinates: {lat: pickupLat, lon: pickupLon}})
                : null,
            hasDropOffCoords
                ? reverseGeocode({coordinates: {lat: dropOffLat, lon: dropOffLon}})
                : null,
        ]);

        const updates: Partial<OrderDraft> = {};

        if (pickupResult.status === "fulfilled" && pickupResult.value) {
            updates.pickup = {
                location: pickupResult.value as LocationWithCoordinates,
                type: "SEARCH",
                complete: true,
            };
        }
        if (dropoffResult.status === "fulfilled" && dropoffResult.value) {
            updates.dropoff = {
                location: dropoffResult.value as LocationWithCoordinates,
                type: "SEARCH",
                complete: true,
            };
        }
        return updates;
        // Coords are parsed from URL on mount and don't change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPickupCoords, hasDropOffCoords]);

    return {hasUrlParams, urlDraft, hasPickupCoords, hasDropOffCoords, resolveLocations};
}
